/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';
import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { CatalogItem } from '@ibm-watson/shared/ts/catalog';
import {
  AUTHENTICATION_METHODS,
  OAUTH2_FLOW_TYPES
} from '../components/catalog-openapi/constants';
import {
  parseSecuritySchemeAuth,
  parseSecuritySchemePrivateAuth,
  serverVariablesValid,
  setServerVariableDefaults
} from '../components/catalog-openapi/helpers';
import { FIELDS } from '../components/catalog-openapi/schema';
import { defineNewCatalogItem } from '../store/catalog/reducer';
import { selectCatalogItemById } from '../store/catalog/selectors';
import { MAX_PASSWORD_LENGTH } from '../util/constants';
import { useFormHelpers } from './useFormHelpers';

/**
 * useCatalogItemHelper custom hook
 *
 * This custom hook is used to derive few helper methods like parseSpecification, setNewCatalogItem & getCatalogFieldsValid
 * This is mainly used in our custom extensions calculations. Incoming prop formMethods is used mainly in getCatalogFieldsValid
 * for field validation calculations.
 */
export const useCatalogItemHelper = (formMethods, catalogItemId) => {
  const dispatch = useAppDispatch();
  const { isInvalidOrEmpty, getFieldValue } = useFormHelpers(formMethods);

  const setNewCatalogItem = useCallback(
    catalogItem => dispatch(defineNewCatalogItem({ catalogItem })),
    [dispatch]
  );

  const draftCatalogItem = useAppSelector(state =>
    selectCatalogItemById(state, catalogItemId)
  );

  const securitySchemes = _.get(
    draftCatalogItem,
    'specification.body.components.securitySchemes',
    {}
  );

  const parseSpecification = useCallback((catalogItem: CatalogItem) => {
    const settings: any = {};

    _.set(settings, FIELDS.CATALOG_ITEM_ID, catalogItem?.catalog_item_id ?? '');
    _.set(
      settings,
      FIELDS.GENERATED_SERVER_URL,
      catalogItem?.specification?.body?.servers[0]?.url ?? ''
    );
    _.set(
      settings,
      FIELDS.SERVER_URL,
      catalogItem?.specification?.body?.servers[0]?.url ?? ''
    );

    const serverVariables = setServerVariableDefaults(
      catalogItem?.specification?.body?.servers[0]?.variables
    );
    _.set(settings, FIELDS.SERVER_VARIABLES, serverVariables);

    const authentication = parseSecuritySchemeAuth(
      catalogItem?.specification?.body?.components?.securitySchemes
    );
    _.set(settings, FIELDS.AUTHENTICATION, authentication);

    _.set(settings, FIELDS.AUTHENTICATION_METHOD, AUTHENTICATION_METHODS.NONE);

    const privateAuthentication = parseSecuritySchemePrivateAuth(
      catalogItem?.specification?.body?.components?.securitySchemes
    );
    _.set(settings, FIELDS.PRIVATE_AUTHENTICATION, privateAuthentication);

    return settings;
  }, []);

  // This function determins if the catalog fields (custom extension) are valid based on the selected authentication method and input values.
  // This is mainly used to check for valid fields values and enable/disable next button or Save & Exit button for custom extensions
  const getCatalogFieldsValid = useCallback(() => {
    // Get the currently selected authentication method and server variables.
    const authenticationMethod = getFieldValue(FIELDS.AUTHENTICATION_METHOD);
    const authenticationName = getFieldValue(
      FIELDS.AUTHENTICATION_DISPLAY_NAME
    );
    const serverVars = getFieldValue(FIELDS.SERVER_VARIABLES);

    // Initialize fieldsValid to track field validity
    let fieldsValid =
      !isInvalidOrEmpty(FIELDS.GENERATED_SERVER_URL) &&
      (!_.isEmpty(serverVars) || serverVariablesValid(serverVars));

    // Check if the fields are valid based on the selected authentication method.
    if (fieldsValid) {
      switch (authenticationMethod) {
        // No additional fields are required for 'None' authentication.
        case AUTHENTICATION_METHODS.NONE:
          break;
        case AUTHENTICATION_METHODS.BASIC:
          // Check if 'Basic' authentication fields are valid.
          // Password field can be empty, if present should be less than or equal to 512 chars.
          fieldsValid =
            !isInvalidOrEmpty(FIELDS.BASIC.USERNAME) &&
            (getFieldValue(FIELDS.BASIC.PASSWORD)?.length ?? 0) <=
              MAX_PASSWORD_LENGTH;
          break;
        case AUTHENTICATION_METHODS.BEARER:
          // Check if 'Bearer' authentication field is valid.
          fieldsValid = !isInvalidOrEmpty(FIELDS.BEARER.TOKEN);
          break;
        case AUTHENTICATION_METHODS.API_KEY:
          // Check if 'API Key' authentication fields are valid.
          const currentAuthValues = getFieldValue(FIELDS.APIKEY.AUTH);
          // If there are no values, yet, we know it is not valid.
          if (currentAuthValues) {
            const apiKeys = Object.keys(getFieldValue(FIELDS.APIKEY.AUTH));
            fieldsValid = _.reduce(
              apiKeys,
              (prev: boolean, key: string) =>
                prev && !isInvalidOrEmpty(FIELDS.APIKEY.VALUE(key)),
              true
            );
          } else {
            fieldsValid = false;
          }
          break;
        case AUTHENTICATION_METHODS.OAUTH2:
          // Check if 'OAuth2' authentication fields are valid based on PREFERRED_FLOW
          const preferredFlow = getFieldValue(FIELDS.OAUTH2.PREFERRED_FLOW);
          switch (preferredFlow) {
            case OAUTH2_FLOW_TYPES.PASSWORD:
              fieldsValid =
                !isInvalidOrEmpty(FIELDS.OAUTH2.USERNAME(preferredFlow)) &&
                !isInvalidOrEmpty(FIELDS.OAUTH2.PASSWORD(preferredFlow)) &&
                !isInvalidOrEmpty(FIELDS.OAUTH2.CLIENT_ID(preferredFlow)) &&
                !isInvalidOrEmpty(FIELDS.OAUTH2.CLIENT_SECRET(preferredFlow));
              break;
            case OAUTH2_FLOW_TYPES.CLIENT_CREDENTIALS:
              fieldsValid =
                !isInvalidOrEmpty(FIELDS.OAUTH2.CLIENT_ID(preferredFlow)) &&
                !isInvalidOrEmpty(FIELDS.OAUTH2.CLIENT_SECRET(preferredFlow));
              break;
            case OAUTH2_FLOW_TYPES.AUTHORIZATION_CODE:
              fieldsValid =
                !isInvalidOrEmpty(FIELDS.OAUTH2.CLIENT_ID(preferredFlow)) &&
                !isInvalidOrEmpty(FIELDS.OAUTH2.CLIENT_SECRET(preferredFlow)) &&
                !isInvalidOrEmpty(FIELDS.OAUTH2.AUTHORIZATION_CODE);
              break;
            default:
              // For the case of OAUTH2 custom flow, check for the secret fields are not invalid or empty
              const secretKeys =
                securitySchemes[
                  authenticationName
                    ? authenticationName
                    : AUTHENTICATION_METHODS.OAUTH2
                ]?.flows?.[preferredFlow]?.secretKeys;

              // Iterate over the secrets array and check if all the values are not invalid or empty
              if (secretKeys?.length) {
                for (let i = 0; i < secretKeys.length; i++) {
                  const secretField = secretKeys[i];
                  if (
                    isInvalidOrEmpty(
                      FIELDS.OAUTH2.CUSTOM.SECRET_VALUE(
                        preferredFlow,
                        secretField
                      )
                    )
                  ) {
                    fieldsValid = false;
                    // Terminate the loop since we found one invalid or empty secret field
                    break;
                  }
                }
              }
              break;
          }
          break;
        default:
          fieldsValid = false;
          break;
      }
    }
    return fieldsValid;
  }, [getFieldValue, isInvalidOrEmpty, securitySchemes]);

  return {
    parseSpecification,
    setNewCatalogItem,
    getCatalogFieldsValid
  };
};
